import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import OfferPage from "@/pages/OfferPage";
import StageTambour from "@/pages/StageTambour";
import Boutique from "@/pages/Boutique";
import ProductDetail from "@/pages/ProductDetail";
import Agenda from "@/pages/Agenda";
import SoinDuMoment from "@/pages/SoinDuMoment";
import SuperLune from "@/pages/SuperLune";
import { StickyStageCta } from "@/components/StickyStageCta";
import EditionsLimitees from "@/pages/EditionsLimitees";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Compte from "@/pages/Compte";
import Faq from "@/pages/Faq";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Confirmation from "@/pages/Confirmation";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Legal from "@/pages/Legal";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import GiftCard from "@/pages/GiftCard";
import Solde from "@/pages/Solde";
import Suivi from "@/pages/Suivi";
import Wyrd from "@/pages/Wyrd";
import WyrdActivation from "@/pages/WyrdActivation";
import Formation from "@/pages/Formation";
import FormationLearn from "@/pages/FormationLearn";
import Galerie from "@/pages/Galerie";
import EventDetail from "@/pages/EventDetail";
import AEcouter from "@/pages/AEcouter";
import LeCarnetDesMondes from "@/pages/LeCarnetDesMondes";
import CreerSonTambour from "@/pages/CreerSonTambour";
import { AssistantBubble } from "@/components/AssistantBubble";
import Admin from "@/pages/Admin";
import Participants from "@/pages/Participants";
import TambourSurMesure from "@/pages/TambourSurMesure";
import TambourPourStructures from "@/pages/TambourPourStructures";
import { LegacyRedirect } from "@/lib/redirects";
import { AuthCallback } from "@/components/AuthCallback";
import { LocaleProvider, localePath } from "@/context/LocaleContext";
import { ConsentBanner } from "@/components/ConsentBanner";
import { initializeMeasurement } from "@/lib/analytics";

const CreerMonTambour = () => {
  const { hash } = useLocation();
  const h = hash === "#dates" || hash === "#individuel" ? hash : "#reserver";
  return <Navigate to={`/offres/stage-fabrication-tambour${h}`} replace />;
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      let t;
      let essais = 0;
      let annule = false;
      const chercher = () => {
        if (annule) return;
        let id;
        try { id = decodeURIComponent(hash.slice(1)); } catch { id = hash.slice(1); }
        const cible = document.getElementById(id);
        if (cible) {
          cible.scrollIntoView({ behavior: essais > 1 ? "auto" : "smooth", block: "start" });
          return;
        }
        if (essais++ < 20) t = setTimeout(chercher, 250);
      };
      t = setTimeout(chercher, 100);
      return () => { annule = true; clearTimeout(t); };
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const english = location.pathname === "/en" || location.pathname.startsWith("/en/");
  const routesLocation = english ? { ...location, pathname: localePath(location.pathname) } : location;
  if (english && routesLocation.pathname.startsWith("/admin")) return <Navigate to={`${routesLocation.pathname}${location.search}${location.hash}`} replace />;

  return (
    <LocaleProvider>
      <ScrollManager />
      <div className="min-h-screen flex flex-col bg-sand">
        <Header />
        <main className="flex-1">
          <Routes location={routesLocation}>
            <Route path="/" element={<Home />} />
            <Route path="/offres/stage-fabrication-tambour" element={<StageTambour />} />
            <Route path="/offres/:slug" element={<OfferPage />} />
            <Route path="/boutique" element={<Boutique />} />
            <Route path="/boutique/tambour/:id" element={<ProductDetail />} />
            <Route path="/agenda" element={<Agenda />} />
            <Route path="/evenements/:slug" element={<EventDetail />} />
            <Route path="/soin" element={<SoinDuMoment />} />
            <Route path="/super-lune" element={<SuperLune />} />
            <Route path="/tambours/editions-limitees" element={<EditionsLimitees />} />
            <Route path="/creer-mon-tambour" element={<CreerMonTambour />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/le-carnet-des-mondes" element={<LeCarnetDesMondes />} />
            <Route path="/creer-son-tambour" element={<CreerSonTambour />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/compte" element={<Compte />} />
            <Route path="/compte/reinitialiser" element={<Compte reset />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/carte-cadeau" element={<GiftCard />} />
            <Route path="/solde/:id" element={<Solde />} />
            <Route path="/suivi/:id" element={<Suivi />} />
            <Route path="/le-wyrd" element={<Wyrd />} />
            <Route path="/wyrd/activer" element={<WyrdActivation />} />
            <Route path="/formation" element={<Formation />} />
            <Route path="/formation/:courseId" element={<FormationLearn />} />
            <Route path="/tambours-realises" element={<Galerie />} />
            <Route path="/tambour-sur-mesure" element={<TambourSurMesure />} />
            <Route path="/tambour-pour-structures" element={<TambourPourStructures />} />
            <Route path="/a-ecouter" element={<AEcouter />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/tambour-funnel" element={<Admin />} />
            <Route path="/admin/participants/:eventId" element={<Participants />} />
            <Route path="/panier" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/confirmation" element={<Confirmation />} />
            <Route path="/paiement/succes" element={<PaymentSuccess />} />
            <Route path="/paiement/annule" element={<PaymentCancel />} />
            <Route path="/mentions-legales" element={<Legal />} />
            <Route path="/confidentialite" element={<Legal />} />
            <Route path="/cgv" element={<Legal />} />
            <Route path="*" element={<LegacyRedirect />} />
          </Routes>
        </main>
        <Footer />
        <AssistantBubble />
        <StickyStageCta />
      </div>
    </LocaleProvider>
  );
}

function App() {
  useEffect(() => {
    initializeMeasurement();
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <CartProvider>
      <BrowserRouter>
        <AppRouter />
        <ConsentBanner />
        <Toaster position="bottom-center" toastOptions={{ style: { background: "#2C221E", color: "#F5F2EB", border: "1px solid #5C4033" } }} />
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
